/**
 * The particle pool.
 *
 * Geometry Wars sells its explosions on two things: density and colour decay.
 * Density is a budget problem — 24,000 live particles in one draw call, a
 * structure-of-arrays pool, and no allocation once boot is over. Colour decay
 * is an art problem: a particle that only fades its alpha looks like a cheap
 * sprite dissolving, while a particle that starts hot *in its own hue*, settles
 * onto its phosphor and then sinks into a deep saturated ember looks like
 * something that was actually burning. This does the second one. Note the hue
 * part — an achromatic birth flash costs the burst its colour over exactly the
 * frames the bloom pass cares about.
 *
 * Layout notes:
 *  - The pool is kept compacted. Killing a particle swaps the tail into its
 *    slot, so the live range is always `[0, n)` and the per-frame upload is one
 *    contiguous `bufferSubData` instead of a scatter.
 *  - Colour and gain only change when a particle is born or gets swapped, so
 *    that attribute uploads a dirty range rather than the whole prefix.
 *  - Rendering is `THREE.Points`. Rotation comes from rotating `gl_PointCoord`
 *    in the fragment shader, which keeps the chip and glyph kinds spinning
 *    without needing instanced quads and four times the vertex work.
 */

import * as THREE from 'three';
import { PARTICLES } from '@/core/config';
import { perf } from '@/core/perf';
import { INTENSITY, PHOSPHOR } from '@/core/palette';
import type { PhosphorName } from '@/core/palette';
import { seedFrom } from '@/core/rng';
import type { ParticleKind, ParticleSpawn, RenderSystem, System, World } from '@/core/types';

/** Kind → shader branch index. Order must match the fragment shader. */
const KIND_ID: Record<ParticleKind, number> = {
  spark: 0,
  debris: 1,
  smoke: 2,
  ring: 3,
  glyph: 4,
  muzzle: 5,
  trailDot: 6,
};

/** Simultaneous gravity wells. Black holes and boss vortices. */
const MAX_WELLS = 8;

interface PresetDef {
  kind: ParticleKind;
  colour: PhosphorName;
  gain: number;
  /** Speed range, buffer px per second. */
  spd0: number;
  spd1: number;
  life0: number;
  life1: number;
  size0: number;
  size1: number;
  /** Velocity lost per second, as a fraction. */
  drag: number;
  /** Positive falls (y is down in buffer space). */
  gravity: number;
  /** Peak spin, radians per second. */
  spin: number;
  /** Half-angle of the emission cone. PI is omnidirectional. */
  cone: number;
  /** Accent ring particles fired once per burst. */
  rings: number;
  /** Ring diameter in buffer pixels at its widest. */
  ringSize: number;
  ringLife: number;
  ringGain: number;
  /** Accent smoke puffs fired once per burst. */
  puffs: number;
}

const P = Math.PI;

/**
 * Preset table. Every burst in the game comes from here, so a tuning pass is
 * one edit per effect rather than a hunt through call sites.
 */
const PRESETS = {
  /** Body comes apart. Fast omnidirectional shrapnel plus one shock ring. */
  enemyDeath: {
    kind: 'spark', colour: 'white', gain: 1.35,
    spd0: 55, spd1: 210, life0: 0.30, life1: 0.78,
    size0: 1.5, size1: 3.2, drag: 3.0, gravity: 8, spin: 0, cone: P,
    rings: 1, ringSize: 22, ringLife: 0.28, ringGain: 1.4, puffs: 2,
  },
  /** Cheap, tight, directional. Fires on every single hit, so it must be small. */
  enemyHit: {
    kind: 'spark', colour: 'white', gain: 1.0,
    spd0: 35, spd1: 130, life0: 0.10, life1: 0.26,
    size0: 1.0, size1: 2.2, drag: 5.5, gravity: 0, spin: 0, cone: 1.1,
    rings: 0, ringSize: 0, ringLife: 0, ringGain: 0, puffs: 0,
  },
  /** Barrel flash. Very short, very bright, tight cone along the shot. */
  towerMuzzle: {
    kind: 'muzzle', colour: 'white', gain: 1.7,
    spd0: 70, spd1: 190, life0: 0.05, life1: 0.15,
    size0: 2.0, size1: 4.5, drag: 9.0, gravity: 0, spin: 0, cone: 0.42,
    rings: 0, ringSize: 0, ringLife: 0, ringGain: 0, puffs: 0,
  },
  /** Splash damage. Two rings, heavy shrapnel, smoke that outlives the flash. */
  explosion: {
    kind: 'spark', colour: 'yellow', gain: 1.6,
    spd0: 70, spd1: 320, life0: 0.22, life1: 0.72,
    size0: 2.0, size1: 4.6, drag: 3.4, gravity: 22, spin: 0, cone: P,
    rings: 2, ringSize: 46, ringLife: 0.34, ringGain: 1.8, puffs: 6,
  },
  /** An enemy reached the exit. Falling red debris — reads as loss, not as a kill. */
  leak: {
    kind: 'debris', colour: 'red', gain: 1.3,
    spd0: 25, spd1: 95, life0: 0.55, life1: 1.20,
    size0: 1.5, size1: 3.0, drag: 1.1, gravity: 130, spin: 7.5, cone: P,
    rings: 1, ringSize: 26, ringLife: 0.4, ringGain: 1.1, puffs: 0,
  },
  /** Tower upgraded. Rises instead of falling; that is the whole read. */
  upgrade: {
    kind: 'glyph', colour: 'cyan', gain: 1.5,
    spd0: 18, spd1: 72, life0: 0.45, life1: 1.05,
    size0: 2.0, size1: 3.6, drag: 1.6, gravity: -55, spin: 2.2, cone: P,
    rings: 1, ringSize: 30, ringLife: 0.45, ringGain: 1.3, puffs: 0,
  },
  /** Boss changes phase. The biggest thing on screen; three rings and a wall. */
  bossPhase: {
    kind: 'spark', colour: 'magenta', gain: 1.9,
    spd0: 90, spd1: 360, life0: 0.5, life1: 1.4,
    size0: 2.0, size1: 5.0, drag: 2.2, gravity: 0, spin: 0, cone: P,
    rings: 3, ringSize: 78, ringLife: 0.7, ringGain: 2.2, puffs: 8,
  },
} as const satisfies Record<string, PresetDef>;

export type ParticlePreset = keyof typeof PRESETS;

export interface ParticleSystemOptions {
  /** Pool size multiplier, from the quality tier. */
  scale?: number;
  seed?: string;
}

export class ParticleSystem implements System, RenderSystem {
  readonly name = 'particles';

  /** Add this to the scene. One draw call for the whole pool. */
  readonly object: THREE.Points;

  private readonly cap: number;
  private n = 0;

  // ── pool (SoA) ──
  private readonly px: Float32Array;
  private readonly py: Float32Array;
  private readonly ppx: Float32Array;
  private readonly ppy: Float32Array;
  private readonly vx: Float32Array;
  private readonly vy: Float32Array;
  private readonly life: Float32Array;
  private readonly maxLife: Float32Array;
  private readonly size: Float32Array;
  private readonly cr: Float32Array;
  private readonly cg: Float32Array;
  private readonly cb: Float32Array;
  private readonly gain: Float32Array;
  private readonly drag: Float32Array;
  private readonly grav: Float32Array;
  private readonly spin: Float32Array;
  private readonly rot: Float32Array;
  private readonly kind: Uint8Array;

  // ── gravity wells ──
  private readonly wellX = new Float32Array(MAX_WELLS);
  private readonly wellY = new Float32Array(MAX_WELLS);
  private readonly wellS = new Float32Array(MAX_WELLS);
  private readonly wellR = new Float32Array(MAX_WELLS);
  private readonly wellT = new Float32Array(MAX_WELLS);
  private wellCount = 0;

  // ── GPU buffers ──
  private readonly posArr: Float32Array;
  private readonly colArr: Float32Array;
  private readonly parArr: Float32Array;
  private readonly velArr: Float32Array;
  private readonly posAttr: THREE.BufferAttribute;
  private readonly colAttr: THREE.BufferAttribute;
  private readonly parAttr: THREE.BufferAttribute;
  private readonly velAttr: THREE.BufferAttribute;
  private readonly geometry: THREE.BufferGeometry;
  private readonly material: THREE.ShaderMaterial;

  private colLo = 0;
  private colHi = -1;
  private spawnCursor = 0;
  private readonly rand: () => number;

  private baseDensity = 1;

  /**
   * Burst counts are multiplied by this. The quality tier writes it; the
   * adaptive governor multiplies on top, which is why it reads back as a
   * product rather than as what was last assigned.
   *
   * The pool is never resized — a burst that would have been 40 sparks is 24,
   * and nothing that is already alive is touched.
   */
  get density(): number {
    return this.baseDensity * perf.caps.particleScale;
  }

  set density(value: number) {
    this.baseDensity = value;
  }

  /** Snap point centres to buffer texel centres. Keeps sprites crisp, not soft. */
  pixelSnap = true;
  /** Particles dropped since boot because the pool was full. Watch it in dev. */
  dropped = 0;

  constructor(opts: ParticleSystemOptions = {}) {
    const scale = opts.scale ?? 1;
    this.cap = Math.max(256, Math.floor(PARTICLES.max * scale));
    this.density = scale;
    this.rand = seedFrom(opts.seed ?? 'pixeltd-fx');

    const c = this.cap;
    this.px = new Float32Array(c);
    this.py = new Float32Array(c);
    this.ppx = new Float32Array(c);
    this.ppy = new Float32Array(c);
    this.vx = new Float32Array(c);
    this.vy = new Float32Array(c);
    this.life = new Float32Array(c);
    this.maxLife = new Float32Array(c);
    this.size = new Float32Array(c);
    this.cr = new Float32Array(c);
    this.cg = new Float32Array(c);
    this.cb = new Float32Array(c);
    this.gain = new Float32Array(c);
    this.drag = new Float32Array(c);
    this.grav = new Float32Array(c);
    this.spin = new Float32Array(c);
    this.rot = new Float32Array(c);
    this.kind = new Uint8Array(c);

    this.posArr = new Float32Array(c * 3);
    this.colArr = new Float32Array(c * 4);
    this.parArr = new Float32Array(c * 4);
    this.velArr = new Float32Array(c * 2);

    this.posAttr = new THREE.BufferAttribute(this.posArr, 3);
    this.colAttr = new THREE.BufferAttribute(this.colArr, 4);
    this.parAttr = new THREE.BufferAttribute(this.parArr, 4);
    this.velAttr = new THREE.BufferAttribute(this.velArr, 2);
    this.posAttr.setUsage(THREE.DynamicDrawUsage);
    this.colAttr.setUsage(THREE.DynamicDrawUsage);
    this.parAttr.setUsage(THREE.DynamicDrawUsage);
    this.velAttr.setUsage(THREE.DynamicDrawUsage);

    this.geometry = new THREE.BufferGeometry();
    this.geometry.setAttribute('position', this.posAttr);
    this.geometry.setAttribute('aColour', this.colAttr);
    this.geometry.setAttribute('aParams', this.parAttr);
    this.geometry.setAttribute('aVel', this.velAttr);
    this.geometry.setDrawRange(0, 0);
    // Positions are rewritten every frame; a stale bounding sphere would cull us.
    this.geometry.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e6);

    this.material = new THREE.ShaderMaterial({
      uniforms: { uSnap: { value: 1 } },
      vertexShader: PARTICLE_VERT,
      fragmentShader: PARTICLE_FRAG,
      transparent: true,
      depthTest: false,
      depthWrite: false,
      blending: THREE.CustomBlending,
      blendSrc: THREE.OneFactor,
      blendDst: THREE.OneFactor,
      blendEquation: THREE.AddEquation,
    });

    this.object = new THREE.Points(this.geometry, this.material);
    this.object.frustumCulled = false;
    this.object.matrixAutoUpdate = false;
    this.object.renderOrder = 40;
    this.object.name = 'particles';
  }

  get live(): number {
    return this.n;
  }

  get capacity(): number {
    return this.cap;
  }

  // ────────────────────────────────────────────────────────────────── spawn ──

  /** Adds one particle. Silently drops when the pool is full — never grows. */
  spawn(s: ParticleSpawn): void {
    if (this.n >= this.cap) {
      this.dropped++;
      return;
    }
    const i = this.n++;
    const base = PHOSPHOR[s.colour];
    const g = INTENSITY[s.colour] * (s.gain ?? 1);

    this.px[i] = s.x;
    this.py[i] = s.y;
    this.ppx[i] = s.x;
    this.ppy[i] = s.y;
    this.vx[i] = s.vx;
    this.vy[i] = s.vy;
    this.life[i] = s.life;
    this.maxLife[i] = s.life > 1e-4 ? s.life : 1e-4;
    this.size[i] = s.size;
    this.cr[i] = base[0];
    this.cg[i] = base[1];
    this.cb[i] = base[2];
    this.gain[i] = g;
    this.drag[i] = s.drag ?? 0;
    this.grav[i] = s.gravity ?? 0;
    this.spin[i] = s.spin ?? 0;
    this.rot[i] = 0;
    this.kind[i] = KIND_ID[s.kind];
    this.markColour(i);
  }

  /**
   * Fires a preset. `dir` aims the cone for directional presets and is ignored
   * by the omnidirectional ones; `colour` overrides the preset's phosphor so an
   * enemy dies in its own hue.
   */
  burst(
    x: number,
    y: number,
    count: number,
    preset: ParticlePreset,
    colour?: PhosphorName,
    dir = 0,
    speedScale = 1,
  ): void {
    const d = PRESETS[preset];
    const hue = colour ?? d.colour;
    const total = Math.max(1, Math.round(count * this.density));
    const r = this.rand;

    for (let k = 0; k < total; k++) {
      const a = dir + (r() * 2 - 1) * d.cone;
      // sqrt keeps the speed distribution area-uniform, so a burst reads as a
      // filled sphere of debris instead of a hollow shell.
      const sp = (d.spd0 + (d.spd1 - d.spd0) * Math.sqrt(r())) * speedScale;
      this.scratch.x = x;
      this.scratch.y = y;
      this.scratch.vx = Math.cos(a) * sp;
      this.scratch.vy = Math.sin(a) * sp;
      this.scratch.life = d.life0 + (d.life1 - d.life0) * r();
      this.scratch.size = d.size0 + (d.size1 - d.size0) * r();
      this.scratch.colour = hue;
      this.scratch.gain = d.gain;
      this.scratch.kind = d.kind;
      this.scratch.drag = d.drag;
      this.scratch.gravity = d.gravity;
      this.scratch.spin = d.spin === 0 ? 0 : (r() * 2 - 1) * d.spin;
      this.spawn(this.scratch);
      // Chips need a starting angle or the whole burst spins in lockstep.
      if (d.spin !== 0 && this.n > 0) this.rot[this.n - 1] = r() * Math.PI * 2;
    }

    // Stacked rings are staggered in size and life, never in colour: a white
    // ring reads as a blowout, and the shader already flashes white at birth.
    for (let k = 0; k < d.rings; k++) {
      this.scratch.x = x;
      this.scratch.y = y;
      this.scratch.vx = 0;
      this.scratch.vy = 0;
      this.scratch.life = d.ringLife * (1 + k * 0.4);
      this.scratch.size = d.ringSize * (1 + k * 0.25);
      this.scratch.colour = hue;
      this.scratch.gain = d.ringGain / (1 + k * 0.8);
      this.scratch.kind = 'ring';
      this.scratch.drag = 0;
      this.scratch.gravity = 0;
      this.scratch.spin = 0;
      this.spawn(this.scratch);
    }

    const puffs = Math.round(d.puffs * this.density);
    for (let k = 0; k < puffs; k++) {
      const a = r() * Math.PI * 2;
      const sp = 8 + r() * 34;
      this.scratch.x = x;
      this.scratch.y = y;
      this.scratch.vx = Math.cos(a) * sp;
      this.scratch.vy = Math.sin(a) * sp - 12;
      this.scratch.life = 0.55 + r() * 0.7;
      this.scratch.size = 5 + r() * 8;
      // Tinted, not grey. Grey haze blooms into a wash; a dim hue blooms into
      // a coloured halo, which is the whole point of the HDR chain.
      this.scratch.colour = hue;
      this.scratch.gain = 0.22;
      this.scratch.kind = 'smoke';
      this.scratch.drag = 1.4;
      this.scratch.gravity = -6;
      this.scratch.spin = 0;
      this.spawn(this.scratch);
    }
  }

  /** Reused spawn descriptor. Filling one object beats allocating 24,000. */
  private readonly scratch: ParticleSpawn = {
    x: 0, y: 0, vx: 0, vy: 0, life: 1, size: 2,
    colour: 'white', gain: 1, kind: 'spark', drag: 0, gravity: 0, spin: 0,
  };

  /**
   * Radial force field. Negative strength sucks particles in, which is how the
   * black-hole tower and boss vortices read.
   */
  addWell(
    x: number,
    y: number,
    strength: number,
    ttl = 0.5,
    radius = PARTICLES.gravityWellRadiusPx,
  ): void {
    if (this.wellCount >= MAX_WELLS) return;
    const i = this.wellCount++;
    this.wellX[i] = x;
    this.wellY[i] = y;
    this.wellS[i] = strength;
    this.wellR[i] = radius;
    this.wellT[i] = ttl;
  }

  clear(): void {
    this.n = 0;
    this.wellCount = 0;
    this.spawnCursor = 0;
    this.colLo = 0;
    this.colHi = -1;
    this.geometry.setDrawRange(0, 0);
  }

  // ─────────────────────────────────────────────────────────────────── step ──

  step(dt: number, world: World): void {
    this.ingest(world);

    // Whole-buffer memcpy beats a bounds-checked loop and never allocates.
    this.ppx.set(this.px);
    this.ppy.set(this.py);

    const { px, py, vx, vy, life, drag, grav, spin, rot } = this;

    for (let w = 0; w < this.wellCount; w++) {
      this.wellT[w] -= dt;
      if (this.wellT[w] <= 0) {
        const last = --this.wellCount;
        this.wellX[w] = this.wellX[last];
        this.wellY[w] = this.wellY[last];
        this.wellS[w] = this.wellS[last];
        this.wellR[w] = this.wellR[last];
        this.wellT[w] = this.wellT[last];
        w--;
      }
    }

    let i = 0;
    while (i < this.n) {
      const rem = life[i] - dt;
      if (rem <= 0) {
        this.kill(i);
        continue;
      }
      life[i] = rem;

      let ax = 0;
      let ay = grav[i];
      for (let w = 0; w < this.wellCount; w++) {
        const dx = this.wellX[w] - px[i];
        const dy = this.wellY[w] - py[i];
        const d2 = dx * dx + dy * dy;
        const rr = this.wellR[w];
        if (d2 > rr * rr || d2 < 1e-4) continue;
        // Linear falloff, not inverse-square: a 1/r² well flings anything that
        // gets close and the effect degenerates into a starburst.
        const d = Math.sqrt(d2);
        const f = (this.wellS[w] * (1 - d / rr)) / d;
        ax += dx * f;
        ay += dy * f;
      }

      let nvx = vx[i] + ax * dt;
      let nvy = vy[i] + ay * dt;
      const dg = drag[i];
      if (dg > 0) {
        const damp = 1 - dg * dt;
        const k = damp < 0 ? 0 : damp;
        nvx *= k;
        nvy *= k;
      }
      vx[i] = nvx;
      vy[i] = nvy;
      px[i] += nvx * dt;
      py[i] += nvy * dt;
      if (spin[i] !== 0) rot[i] += spin[i] * dt;
      i++;
    }
  }

  /** Drains the world spawn queue exactly once per entry. */
  private ingest(world: World): void {
    const q = world.fx.particles;
    if (q.length < this.spawnCursor) this.spawnCursor = 0;
    for (let i = this.spawnCursor; i < q.length; i++) this.spawn(q[i]);
    this.spawnCursor = q.length;
  }

  /** Swaps the tail into the dead slot so the live range stays contiguous. */
  private kill(i: number): void {
    const j = --this.n;
    if (i !== j) {
      this.px[i] = this.px[j];
      this.py[i] = this.py[j];
      this.ppx[i] = this.ppx[j];
      this.ppy[i] = this.ppy[j];
      this.vx[i] = this.vx[j];
      this.vy[i] = this.vy[j];
      this.life[i] = this.life[j];
      this.maxLife[i] = this.maxLife[j];
      this.size[i] = this.size[j];
      this.cr[i] = this.cr[j];
      this.cg[i] = this.cg[j];
      this.cb[i] = this.cb[j];
      this.gain[i] = this.gain[j];
      this.drag[i] = this.drag[j];
      this.grav[i] = this.grav[j];
      this.spin[i] = this.spin[j];
      this.rot[i] = this.rot[j];
      this.kind[i] = this.kind[j];
      this.markColour(i);
    }
  }

  private markColour(i: number): void {
    if (this.colHi < this.colLo) {
      this.colLo = i;
      this.colHi = i;
      return;
    }
    if (i < this.colLo) this.colLo = i;
    if (i > this.colHi) this.colHi = i;
  }

  // ─────────────────────────────────────────────────────────────────── sync ──

  sync(_world: World, alpha: number): void {
    const a = alpha < 0 ? 0 : alpha > 1 ? 1 : alpha;
    const n = this.n;
    const { posArr, parArr, colArr, velArr } = this;

    for (let i = 0; i < n; i++) {
      const o = i * 3;
      posArr[o] = this.ppx[i] + (this.px[i] - this.ppx[i]) * a;
      posArr[o + 1] = this.ppy[i] + (this.py[i] - this.ppy[i]) * a;
      posArr[o + 2] = 0;

      const v = i * 2;
      velArr[v] = this.vx[i];
      velArr[v + 1] = this.vy[i];

      const p = i * 4;
      parArr[p] = this.size[i];
      parArr[p + 1] = this.rot[i];
      parArr[p + 2] = this.kind[i];
      // Age, 0 at birth and 1 at death. Drives both size and the colour ramp.
      parArr[p + 3] = 1 - this.life[i] / this.maxLife[i];
    }

    // Colour is only touched on spawn and on swap, so upload the dirty span.
    if (this.colHi >= this.colLo) {
      const lo = this.colLo;
      const hi = Math.min(this.colHi, this.cap - 1);
      for (let i = lo; i <= hi; i++) {
        const c = i * 4;
        colArr[c] = this.cr[i];
        colArr[c + 1] = this.cg[i];
        colArr[c + 2] = this.cb[i];
        colArr[c + 3] = this.gain[i];
      }
      this.colAttr.clearUpdateRanges();
      this.colAttr.addUpdateRange(lo * 4, (hi - lo + 1) * 4);
      this.colAttr.needsUpdate = true;
      this.colLo = 0;
      this.colHi = -1;
    }

    if (n > 0) {
      this.posAttr.clearUpdateRanges();
      this.posAttr.addUpdateRange(0, n * 3);
      this.posAttr.needsUpdate = true;
      this.parAttr.clearUpdateRanges();
      this.parAttr.addUpdateRange(0, n * 4);
      this.parAttr.needsUpdate = true;
      this.velAttr.clearUpdateRanges();
      this.velAttr.addUpdateRange(0, n * 2);
      this.velAttr.needsUpdate = true;
    }

    this.material.uniforms.uSnap.value = this.pixelSnap ? 1 : 0;
    this.geometry.setDrawRange(0, n);
  }

  dispose(): void {
    this.geometry.dispose();
    this.material.dispose();
  }
}

const PARTICLE_VERT = /* glsl */ `
precision highp float;

attribute vec4 aColour;   // rgb = linear phosphor base, a = HDR gain
attribute vec4 aParams;   // x = size px, y = rotation, z = kind, w = age 0..1
attribute vec2 aVel;      // buffer px per second

uniform float uSnap;

varying vec3 vColour;
varying float vGain;
varying float vAge;
varying float vKind;
varying float vRot;
varying float vSize;
varying vec2 vDir;        // unit velocity direction
varying float vAniso;     // how much to squeeze across the velocity axis

void main() {
  vColour = aColour.rgb;
  vGain = aColour.a;
  vAge = aParams.w;
  vKind = aParams.z;
  vRot = aParams.y;

  // World space is buffer space 1:1, so snapping to a half-integer puts the
  // sprite centre on a texel centre. That is the difference between crisp
  // pixel debris and a smear that betrays the whole CRT conceit.
  vec3 p = position;
  if (uSnap > 0.5) p.xy = floor(p.xy) + 0.5;

  gl_Position = projectionMatrix * modelViewMatrix * vec4(p, 1.0);

  float k = aParams.z;
  float grow = 1.0 - 0.45 * vAge;
  // Rings open from a point to their nominal diameter; smoke swells gently.
  if (k > 2.5 && k < 3.5) grow = 0.15 + 0.90 * vAge;
  else if (k > 1.5 && k < 2.5) grow = 0.65 + 1.20 * vAge;
  float s = aParams.x * grow;

  // Velocity streaks. A death spark leaving at 240px/s used to draw the same
  // round dot as one drifting at 30, which is the most visible single gap
  // against the Geometry Wars reference stills. Only the round kinds stretch —
  // spark (k=0) and trailDot (k=6); a spinning debris chip or a shock ring
  // smeared along its own velocity reads as a bug.
  float roundKind = step(k, 0.5) + step(5.5, k);
  float stretch = 1.0 + min(3.0, length(aVel) / 90.0) * roundKind;
  // Area would be conserved by squeezing on the reciprocal, but base sizes run
  // 1.5-3.2px, so a full reciprocal puts the minor axis under one texel and the
  // streak disappears into the nearest-filtered buffer. Square root keeps it
  // visible while still thinning as it lengthens.
  vAniso = stretch * sqrt(stretch);
  float vm = length(aVel);
  vDir = vm > 1e-4 ? aVel / vm : vec2(1.0, 0.0);
  // The sprite has to be square enough to hold the long axis.
  s *= stretch;

  // Integer point sizes keep the sprite footprint on the pixel grid too.
  gl_PointSize = max(1.0, uSnap > 0.5 ? floor(s + 0.5) : s);
  vSize = gl_PointSize;
}
`;

const PARTICLE_FRAG = /* glsl */ `
precision highp float;

varying vec3 vColour;
varying float vGain;
varying float vAge;
varying float vKind;
varying float vRot;
varying float vSize;
varying vec2 vDir;
varying float vAniso;

void main() {
  vec2 uv = gl_PointCoord * 2.0 - 1.0;
  uv.y = -uv.y;
  float cs = cos(vRot);
  float sn = sin(vRot);
  vec2 p = vec2(uv.x * cs - uv.y * sn, uv.x * sn + uv.y * cs);

  // Radius measured in the velocity frame: along the direction of travel it is
  // the plain sprite coordinate, across it the coordinate is scaled up so the
  // same round mask resolves to an ellipse. Static kinds carry vAniso = 1 and
  // vDir = +x, which collapses this back to length(uv) exactly.
  vec2 q = vec2(dot(uv, vDir), (uv.x * -vDir.y + uv.y * vDir.x) * vAniso);
  float r = length(q);

  float k = vKind;
  float m;
  if (k < 0.5) {
    // spark: soft skirt with a hard hot core. The skirt used to fall off as
    // s², which put everything past the middle of the sprite under the bloom
    // threshold — a 2px core and no reach. Nearly linear now, so the outer
    // half of the sprite still clears the bright pass and the burst carries a
    // halo instead of a dot.
    float s = smoothstep(1.0, 0.0, r);
    m = s * (0.34 + 0.66 * s) + smoothstep(0.50, 0.0, r) * 1.1;
  } else if (k < 1.5) {
    // debris: a spinning chip, edges kept hard on purpose
    float d = max(abs(p.x), abs(p.y));
    m = smoothstep(0.80, 0.58, d);
  } else if (k < 2.5) {
    // smoke: wide and weak, it only exists to carry the bloom skirt
    m = smoothstep(1.0, 0.0, r) * 0.42;
  } else if (k < 3.5) {
    // ring: the band holds a constant pixel width as the sprite grows, so an
    // expanding shockwave thins out instead of inflating into a donut.
    float hw = clamp(3.0 / vSize, 0.04, 0.5);
    float t = smoothstep(hw, 0.0, abs(r - 0.72));
    m = t * t;
  } else if (k < 4.5) {
    // glyph: a filled square, the pixel-art primitive
    float d = max(abs(p.x), abs(p.y));
    m = smoothstep(0.66, 0.48, d);
  } else if (k < 5.5) {
    // muzzle: four-point star, the shape that reads as a gunshot
    float bx = smoothstep(0.34, 0.0, abs(p.y)) * smoothstep(1.0, 0.1, abs(p.x));
    float by = smoothstep(0.34, 0.0, abs(p.x)) * smoothstep(1.0, 0.1, abs(p.y));
    m = max(bx, by) + smoothstep(0.42, 0.0, r);
  } else {
    // trailDot
    m = smoothstep(0.70, 0.0, r);
  }

  if (m <= 0.004) discard;

  // The colour walk. It used to open on pure white and take 28% of the
  // particle's life to reach its phosphor, which meant the brightest, most
  // bloom-visible third of every burst was achromatic — and the bloom chain
  // then spread that white across the frame. Heat is a *level*, not an absence
  // of hue: the ramp now starts above the phosphor and walks down it, ending in
  // a deep saturated ember. Squaring the base crushes the weak channels and
  // leaves the dominant hue standing, which is what a cooling emitter does.
  vec3 hot = vColour * 2.6 + 0.09;
  vec3 mid = vColour;
  vec3 cold = vColour * vColour * 1.4;
  float a = vAge;
  vec3 col = a < 0.28
    ? mix(hot, mid, a / 0.28)
    : mix(mid, cold, (a - 0.28) / 0.72);

  // Was squared, which put a particle at a quarter brightness by half-life and
  // dropped it under the bloom threshold about a fifth of the way through. At
  // 1.5 it holds its light long enough to be photographed, and the burst still
  // ends rather than lingering as haze.
  float fade = 1.0 - a;
  fade *= sqrt(fade);

  gl_FragColor = vec4(col * (vGain * fade * m), 1.0);
}
`;
